import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import {
  blockBackdropClose,
  DataTable,
  FormDialogTitle,
  LoadingState,
  useErrorToast,
  type DataTableColumn,
} from '../components/common';
import { StatisticsRefreshControl } from '../components/statistics/StatisticsRefreshControl';
import { useStoredRefreshInterval } from '../hooks/useStoredRefreshInterval';
import { useEventContext } from '../contexts/EventContext';
import { eventReaderDeviceService, type EventReaderDevice } from '../services/eventReaderDeviceService';
import { timingPointService, type TimingPoint } from '../services/timingPointService';
import { readerDeviceService, type ReaderDevice } from '../services/readerDeviceService';

const tabularNum = { fontVariantNumeric: 'tabular-nums' } as const;

const REFRESH_STORAGE_KEY = 'eventReaderDevices.refreshIntervalMs';
const refreshOptions = [
  { label: '수동', value: 0 },
  { label: '10초', value: 10000 },
  { label: '30초', value: 30000 },
  { label: '1분', value: 60000 },
];

const TrashIcon = () => (
  <svg
    width={15}
    height={15}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

const WarningIcon = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

type DotState = 'off' | 'partial' | 'on';

const DOT_COLORS: Record<DotState, string> = {
  off: '#cbd5e1',
  partial: '#d97706',
  on: '#22c55e',
};

/** 연결/안테나 상태 표시 점. boolean은 off/on 2단계로 취급한다 */
const StatusDot = ({ state }: { state: DotState | boolean }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: DOT_COLORS[typeof state === 'boolean' ? (state ? 'on' : 'off') : state],
    }}
  />
);

/**
 * 리더기 연결 상태 3단계 판정
 * - off: 하트비트가 끊겨 리더기가 살아있지 않음
 * - partial: 리더기는 살아있지만(하트비트 O), 이 대회의 config를 아직 확인 못함
 * - on: 리더기가 살아있고, 이 대회의 config를 실제로 내려받아 확인함
 */
function getConnectionState(row: EventReaderDevice, selectedEventId: number | null): DotState {
  if (row.connection !== 'CONNECTED') return 'off';
  if (selectedEventId != null && row.lastConfigFetchedEventId === selectedEventId) return 'on';
  return 'partial';
}

const CONNECTION_LABELS: Record<DotState, string> = {
  off: '연결 안됨',
  partial: '기기 켜짐',
  on: '연결됨',
};

const CONNECTION_TEXT_COLORS: Record<DotState, string> = {
  off: '#94a3b8',
  partial: '#d97706',
  on: '#16a34a',
};

const CONNECTION_TOOLTIPS: Record<DotState, string> = {
  off: '리더기가 서버와 통신되지 않고 있습니다. 전원과 네트워크 연결을 확인하세요.',
  partial: '리더기는 켜져 있지만, 아직 이 대회의 설정을 내려받지 않았습니다. 리더기에서 대회 설정을 다시 다운로드해 주세요.',
  on: '리더기가 이 대회의 설정을 실제로 내려받아 연결이 확인됐습니다.',
};

/** 배터리 잔량에 따른 경고 색상 */
function batteryColor(battery: number) {
  if (battery <= 20) return '#dc2626';
  if (battery <= 50) return '#d97706';
  return '#16a34a';
}

function renderBattery(row: EventReaderDevice) {
  if (row.battery == null) {
    return <span style={{ color: '#cbd5e1' }}>-</span>;
  }
  return (
    <span style={{ ...tabularNum, color: batteryColor(row.battery), fontWeight: 600 }}>
      {row.battery}%
    </span>
  );
}

function getAntennaTooltipTitle(connected: boolean, working: boolean) {
  if (connected === false) return '연결 끊김';
  return working ? '동작 중' : '정지';
}

function EventReaderDevicesPage() {
  const { selectedEventId } = useEventContext();
  const [devices, setDevices] = useState<EventReaderDevice[]>([]);
  const [timingPoints, setTimingPoints] = useState<TimingPoint[]>([]);
  const [allReaderDevices, setAllReaderDevices] = useState<ReaderDevice[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<EventReaderDevice | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showError, errorToast } = useErrorToast();
  const {
    applyRefreshInterval,
    draftRefreshIntervalMs,
    refreshIntervalMs,
    setDraftRefreshIntervalMs,
  } = useStoredRefreshInterval(REFRESH_STORAGE_KEY);

  // 리더기 상태(연결·배터리·R1~R4)만 다시 불러온다. 자동 새로고침·수동 갱신 공용.
  const refreshDevices = useCallback(
    (eventId: number) => {
      eventReaderDeviceService
        .list(eventId)
        .then((list) => {
          setDevices(list);
          setLastUpdatedAt(Date.now());
        })
        .catch(showError);
    },
    [showError]
  );

  const loadAll = useCallback(
    (eventId: number) => {
      setIsLoading(true);
      Promise.all([
        eventReaderDeviceService.list(eventId).then((list) => {
          setDevices(list);
          setLastUpdatedAt(Date.now());
        }),
        timingPointService.list(eventId).then(setTimingPoints),
        readerDeviceService.list().then(setAllReaderDevices),
      ])
        .catch(showError)
        .finally(() => setIsLoading(false));
    },
    [showError]
  );

  useEffect(() => {
    if (selectedEventId == null) return;
    loadAll(selectedEventId);
  }, [selectedEventId, loadAll]);

  // 선택한 주기로 리더기 상태를 자동 갱신한다. '수동'(0)이면 폴링하지 않는다.
  useEffect(() => {
    if (selectedEventId == null || refreshIntervalMs <= 0) return;
    const timer = globalThis.setInterval(() => refreshDevices(selectedEventId), refreshIntervalMs);
    return () => globalThis.clearInterval(timer);
  }, [selectedEventId, refreshIntervalMs, refreshDevices]);

  const handleTimingPointChange = async (row: EventReaderDevice, value: string) => {
    if (selectedEventId == null) return;
    const timingPointId = value === '' ? null : Number(value);
    try {
      await eventReaderDeviceService.updateTimingPoint(selectedEventId, row.readerDeviceId, timingPointId);
      const name = timingPoints.find((tp) => tp.id === timingPointId)?.timingPointName ?? null;
      setDevices((prev) =>
        prev.map((d) => (d.readerDeviceId === row.readerDeviceId ? { ...d, timingPointName: name } : d))
      );
    } catch (error) {
      showError(error);
    }
  };

  const handleToggle = async (row: EventReaderDevice) => {
    if (selectedEventId == null) return;
    const next = !row.mapped;
    try {
      await eventReaderDeviceService.setConfig(selectedEventId, row.readerDeviceId, next);
      setDevices((prev) =>
        prev.map((d) => (d.readerDeviceId === row.readerDeviceId ? { ...d, mapped: next } : d))
      );
    } catch (error) {
      showError(error);
    }
  };

  const confirmDelete = async () => {
    if (deleteTarget == null || selectedEventId == null) return;
    try {
      await eventReaderDeviceService.remove(selectedEventId, deleteTarget.readerDeviceId);
      setDevices((prev) => prev.filter((d) => d.readerDeviceId !== deleteTarget.readerDeviceId));
      setDeleteTarget(null);
    } catch (error) {
      showError(error);
    }
  };

  const applyAll = async (active: boolean) => {
    if (selectedEventId == null) return;
    try {
      if (active) {
        await eventReaderDeviceService.activateAll(selectedEventId);
      } else {
        await eventReaderDeviceService.deactivateAll(selectedEventId);
      }
      const list = await eventReaderDeviceService.list(selectedEventId);
      setDevices(list);
    } catch (error) {
      showError(error);
    }
  };

  const available = allReaderDevices.filter(
    (rd) => !devices.some((d) => d.readerDeviceId === rd.id)
  );

  const openAdd = () => {
    setSelectedIds([]);
    setIsAddOpen(true);
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const confirmAdd = async () => {
    if (selectedEventId == null || selectedIds.length === 0) return;
    try {
      await eventReaderDeviceService.add(selectedEventId, selectedIds);
      const list = await eventReaderDeviceService.list(selectedEventId);
      setDevices(list);
      setIsAddOpen(false);
    } catch (error) {
      showError(error);
    }
  };

  const columns: DataTableColumn<EventReaderDevice>[] = [
    {
      key: 'code',
      header: '리더기 코드',
      render: (row) => <span style={{ fontWeight: 600 }}>{row.code}</span>,
    },
    {
      key: 'timingPointName',
      header: '계측 지점',
      width: 170,
      render: (row) => {
        const currentId = timingPoints.find((tp) => tp.timingPointName === row.timingPointName)?.id ?? '';
        const isUnset = currentId === '';
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Select
              size="small"
              displayEmpty
              value={currentId === '' ? '' : String(currentId)}
              onChange={(e) => handleTimingPointChange(row, e.target.value)}
              sx={{
                fontSize: 12,
                height: 30,
                minWidth: 130,
                backgroundColor: isUnset ? '#fffbeb' : '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isUnset ? '#f59e0b' : undefined,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isUnset ? '#d97706' : undefined,
                },
              }}
            >
              <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                계측 지점 선택
              </MenuItem>
              {timingPoints.map((tp) => (
                <MenuItem key={tp.id} value={String(tp.id)} sx={{ fontSize: 12 }}>
                  {tp.timingPointName}
                </MenuItem>
              ))}
            </Select>
            {isUnset && (
              <Tooltip title="계측 지점이 설정되지 않아 이 리더기가 수신한 기록은 결과 계산에 반영되지 않습니다.">
                <Box component="span" sx={{ display: 'inline-flex', color: '#d97706', cursor: 'default' }}>
                  <WarningIcon />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      key: 'connection',
      header: '연결 상태',
      width: 100,
      render: (row) => {
        const state = getConnectionState(row, selectedEventId);
        return (
          <Tooltip title={CONNECTION_TOOLTIPS[state]}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'default' }}>
              <StatusDot state={state} />
              <span style={{ fontSize: 12, color: CONNECTION_TEXT_COLORS[state] }}>
                {CONNECTION_LABELS[state]}
              </span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'battery',
      header: '배터리',
      width: 90,
      render: renderBattery,
    },
    {
      key: 'r1r2',
      header: 'R1~R4',
      width: 240,
      render: (row) => {
        const connected = row.connection === 'CONNECTED';
        const dot = (working: boolean) => (
          <Tooltip title={getAntennaTooltipTitle(connected, working)}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <StatusDot state={connected && working} />
            </Box>
          </Tooltip>
        );
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 12, color: '#475569' }}>
            <span>R1:</span>
            {dot(row.r1)}
            <span>R2:</span>
            {dot(row.r2)}
            {/* R3/R4는 4리더 모델만 보낸다. 2리더 모델은 null이라 정지로 표시한다. */}
            <span>R3:</span>
            {dot(row.r3 ?? false)}
            <span>R4:</span>
            {dot(row.r4 ?? false)}
          </Box>
        );
      },
    },
    {
      key: 'mapped',
      header: '현재 대회 설정',
      width: 150,
      render: (row) => (
        <Tooltip title="활성화시 이 리더기의 ‘현재 대회’가 이 대회로 지정됩니다. (리더기 연결 시 반영)">
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            <Switch size="small" checked={row.mapped} onChange={() => handleToggle(row)} />
            <Box
              component="span"
              sx={{ fontSize: 12, minWidth: 34, textAlign: 'left', color: row.mapped ? '#16a34a' : '#94a3b8' }}
            >
              {row.mapped ? '설정됨' : '미설정'}
            </Box>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: '_delete',
      header: '',
      width: 56,
      render: (row) => (
        <Tooltip title="이 대회에서 리더기 제거">
          <IconButton
            size="small"
            aria-label="리더기 제거"
            sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626' } }}
            onClick={() => setDeleteTarget(row)}
          >
            <TrashIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (selectedEventId == null) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <p className="page-section">Event</p>
          <h1>대회 리더기 설정</h1>
          <Box sx={{ mt: 3, color: '#8a94a6', fontSize: 14 }}>
            선택된 대회가 없습니다. 대회 목록에서 대회를 먼저 선택해 주세요.
          </Box>
        </div>
      </section>
    );
  }

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <p className="page-section">Event</p>
        <h1>대회 리더기 설정</h1>
        <p className="page-description">대회에 연결된 리더기와 계측 지점의 매핑을 관리합니다.</p>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mt: 1,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* 좌측: 자동 새로고침 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <StatisticsRefreshControl
              ariaLabel="대회 리더기 갱신 주기"
              className="event-reader-refresh-control"
              draftRefreshIntervalMs={draftRefreshIntervalMs}
              isApplyDisabled={draftRefreshIntervalMs === refreshIntervalMs}
              isRefreshDisabled={isLoading}
              options={refreshOptions}
              updatedAt={lastUpdatedAt ?? 0}
              onApply={applyRefreshInterval}
              onChange={setDraftRefreshIntervalMs}
              onRefresh={() => loadAll(selectedEventId)}
            />
          </Box>

          {/* 우측: 현재 대회 일괄 설정 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ fontSize: 13, color: '#64748b' }}>현재 대회 설정:</Box>
            <Button size="small" variant="contained" disableElevation onClick={() => applyAll(true)}>
              전체 활성화
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyAll(false)}>
              전체 비활성화
            </Button>
          </Box>
        </Box>

        <div className="page-table">
          {isLoading ? (
            <LoadingState message="대회 리더기 목록을 불러오는 중입니다." />
          ) : (
            <DataTable
              columns={columns}
              rows={devices}
              rowKey="readerDeviceId"
              emptyMessage="배정된 리더기가 없습니다. ‘리더기 추가’로 등록해 주세요."
            />
          )}
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 1.5, display: 'block', mx: 'auto' }}
            onClick={openAdd}
          >
            + 리더기 추가
          </Button>
        </div>
      </div>

      {/* 리더기 추가 모달 */}
      <Dialog open={isAddOpen} onClose={blockBackdropClose(() => setIsAddOpen(false))} maxWidth="xs" fullWidth>
        <FormDialogTitle onClose={() => setIsAddOpen(false)} sx={{ fontSize: 16, fontWeight: 700 }}>
          리더기 추가
        </FormDialogTitle>
        <DialogContent dividers>
          {available.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center', color: '#8a94a6', fontSize: 13 }}>
              추가할 수 있는 리더기가 없습니다.
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {available.map((rd) => (
                <FormControlLabel
                  key={rd.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(rd.id)}
                      onChange={() => toggleSelect(rd.id)}
                    />
                  }
                  label={rd.code}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddOpen(false)}>취소</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={selectedIds.length === 0}
            onClick={confirmAdd}
          >
            추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* 리더기 제거 확인 */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>리더기 제거</DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
            ‘{deleteTarget?.code}’ 리더기를 이 대회에서 제거할까요?
            <br />
            배정된 계측 지점와 현재 대회 설정도 함께 해제됩니다.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>취소</Button>
          <Button color="error" variant="contained" disableElevation onClick={confirmDelete}>
            제거
          </Button>
        </DialogActions>
      </Dialog>

      {errorToast}
    </section>
  );
}

export default EventReaderDevicesPage;
